import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;

// Flushes any pending save on unmount (instead of cancelling) so input isn't silently lost on navigation.
// `save` may close over current state — saveRef ensures the latest closure is always invoked.
export function useAutosaveOnBlur(save: () => void, delayMs: number = DEFAULT_DEBOUNCE_MS) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveRef = useRef(save);
  // EWM-936: assigned in an effect rather than during render — writing a ref while
  // rendering is not allowed. Both readers (flush, onBlur) fire from event handlers
  // and timers, i.e. always after commit, so they still see the latest closure.
  useEffect(() => {
    saveRef.current = save;
  });

  const flush = useCallback(() => {
    if (timerRef.current === undefined) return;
    clearTimeout(timerRef.current);
    timerRef.current = undefined;
    saveRef.current();
  }, []);

  const onBlur = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      saveRef.current();
    }, delayMs);
  }, [delayMs]);

  useEffect(() => flush, [flush]); // cleanup = flush: runs flush on unmount to preserve any pending save

  return { onBlur, flush };
}
